import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import ProcedureDTO from '@src/adapters/dto/procedure/ProcedureDTO';
import { useCreateProcedureLazy } from '@src/adapters/hooks/procedures/useCreateProcedure';
import SpecialityAutoComplete from '@src/components/speciality/SpecialityAutoComplete';

interface Props {
  procedure: ProcedureDTO;
  cancel(): void;
  saved(procedure: ProcedureDTO): void;
}

function Edit({ procedure, cancel, saved }: Props) {
  const { execute: save } = useCreateProcedureLazy();
  const { enqueueSnackbar } = useSnackbar();
  const { handleSubmit, control, formState, reset } = useForm<ProcedureDTO>({
    mode: 'all',
  });

  useEffect(() => {
    reset(procedure);
  }, [procedure, reset]);

  const onSubmit = handleSubmit(async (data) => {
    try {
      let proc: ProcedureDTO = {
        id: procedure.id,
        name: data.name,
        procedureShortName: data.procedureShortName,
        variant: data.variant,
        variantShortName: data.variantShortName,
        specialityId: data.specialityId,
        ccsdCode: data.ccsdCode,
        opcsCode: data.opcsCode,
      };
      const result = await save({ procedure: proc });
      saved(result.procedure);
    } catch (err: any) {
      enqueueSnackbar(err.message ? err.message : err, { variant: 'error' });
    }
  });

  return (
    <Container>
      {procedure && (
        <Box>
          <Typography variant="h5" sx={{ paddingBottom: '20px' }}>
            Details
          </Typography>
          <form onSubmit={onSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Controller
                      name="name"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          {...field}
                          label="Name"
                          variant="standard"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="procedureShortName"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          {...field}
                          label="Procedure Short Name"
                          variant="standard"
                          inputProps={{ maxLength: 10 }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="variant"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Variant"
                          variant="standard"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="variantShortName"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          fullWidth
                          {...field}
                          label="Variant Short Name"
                          variant="standard"
                          inputProps={{ maxLength: 10 }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="ccsdCode"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="CCSD Code"
                          variant="standard"
                          inputProps={{ maxLength: 150 }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Controller
                      name="opcsCode"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="OPCS Code"
                          variant="standard"
                          inputProps={{ maxLength: 150 }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <SpecialityAutoComplete
                      control={control}
                      name="specialityId"
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={4}>
                <Button
                  color="secondary"
                  fullWidth
                  variant="outlined"
                  onClick={cancel}>
                  cancel
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  color="primary"
                  fullWidth
                  type="submit"
                  variant="contained"
                  disabled={!formState.isValid}>
                  save
                </Button>
              </Grid>
            </Grid>
          </form>
        </Box>
      )}
    </Container>
  );
}

export default Edit;
